import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { Input } from '@/components/ui/input';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { SettingsCard, SettingsRow, SettingsSection } from '../components/SettingsSection';

interface DeliveryEstimateSectionProps {
  pickPackDays: number;
  transitDays: number;
}

/**
 * Days feeding the estimated delivery date shown on checkout rates. Saves
 * instantly on blur, each field through its own mutation.
 */
export function DeliveryEstimateSection({
  pickPackDays,
  transitDays,
}: DeliveryEstimateSectionProps) {
  const { t } = useTranslation('settings');

  const updatePickPack = useUpdateMerchantSettings();
  const updateTransit = useUpdateMerchantSettings();

  const [pickPackInput, setPickPackInput] = useState(String(pickPackDays));
  const [transitInput, setTransitInput] = useState(String(transitDays));

  // Re-sync the inputs when saved settings arrive/refresh.
  useEffect(() => {
    setPickPackInput(String(pickPackDays));
  }, [pickPackDays]);

  useEffect(() => {
    setTransitInput(String(transitDays));
  }, [transitDays]);

  const saveDays = (
    raw: string,
    saved: number,
    setInput: (value: string) => void,
    mutate: (value: number) => void,
  ) => {
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setInput(String(saved));
      return;
    }
    setInput(String(parsed));
    if (parsed === saved) return;
    mutate(parsed);
  };

  const savePickPack = () =>
    saveDays(pickPackInput, pickPackDays, setPickPackInput, (value) =>
      updatePickPack.mutate(
        { settings: { pick_pack_time_days: value } },
        {
          onSuccess: () => toast.success(t('saved')),
          onError: (error) => {
            setPickPackInput(String(pickPackDays));
            toastProblem(toApiProblem(error));
          },
        },
      ),
    );

  const saveTransit = () =>
    saveDays(transitInput, transitDays, setTransitInput, (value) =>
      updateTransit.mutate(
        { settings: { transit_time_days: value } },
        {
          onSuccess: () => toast.success(t('saved')),
          onError: (error) => {
            setTransitInput(String(transitDays));
            toastProblem(toApiProblem(error));
          },
        },
      ),
    );

  const daysField = (
    value: string,
    onChange: (value: string) => void,
    onBlur: () => void,
    label: string,
  ) => (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        aria-label={label}
        className="h-9 w-20 text-center"
      />
      <span className="text-[13px] text-muted-foreground">
        {t('general.deliveryEstimate.daysUnit')}
      </span>
    </div>
  );

  return (
    <SettingsSection
      title={t('general.deliveryEstimate.title')}
      description={t('general.deliveryEstimate.description')}
    >
      <SettingsCard>
        <SettingsRow
          label={t('general.deliveryEstimate.pickPack')}
          description={t('general.deliveryEstimate.pickPackHint')}
        >
          {daysField(
            pickPackInput,
            setPickPackInput,
            savePickPack,
            t('general.deliveryEstimate.pickPack'),
          )}
        </SettingsRow>
        <SettingsRow
          label={t('general.deliveryEstimate.transit')}
          description={t('general.deliveryEstimate.transitHint')}
        >
          {daysField(
            transitInput,
            setTransitInput,
            saveTransit,
            t('general.deliveryEstimate.transit'),
          )}
        </SettingsRow>
      </SettingsCard>
    </SettingsSection>
  );
}
